<div class="page manual-attendance-page">
  <div class="page-header compact-header">
    <div>
      <h1>{{ 'ATTENDANCE_MANUAL.TITLE' | translate }}</h1>
      <p class="page-subtitle" *ngIf="todayLabel">{{ todayLabel }}</p>
    </div>
    <div class="page-actions">
      <button type="button" class="btn btn-secondary btn-sm" (click)="loadReport()" [disabled]="loading || applying || !!busyStudentId">
        <mat-icon>refresh</mat-icon>
        {{ 'ATTENDANCE_DASH.REFRESH' | translate }}
      </button>
    </div>
  </div>

  <section class="filters-bar" [attr.aria-label]="'ATTENDANCE_MANUAL.FILTERS' | translate">
    <div class="filter-group class-group">
      <app-searchable-select
        class="class-select"
        [(ngModel)]="selectedClassId"
        [options]="classOptions"
        [placeholder]="selectClassLabel"
        [emptyLabel]="selectClassLabel"
        [searchPlaceholder]="searchPlaceholder"
        [allowEmpty]="false"
        (selectionChange)="onClassChange()"
      ></app-searchable-select>
    </div>

    <div class="filter-group search-group">
      <input
        type="search"
        class="search-input"
        [(ngModel)]="search"
        [placeholder]="'ATTENDANCE_DASH.SEARCH' | translate"
        [disabled]="loading || applying"
      />
    </div>

    <div class="inline-stats" *ngIf="report">
      <span>{{ report.summary.present }}/{{ report.summary.total }}</span>
      <span class="muted" *ngIf="hasPending">+{{ pendingCount }}</span>
      <span class="muted">{{ filteredRows.length }}</span>
    </div>
  </section>

  <div class="draft-bar" *ngIf="report">
    <button
      type="button"
      class="btn btn-success btn-sm"
      (click)="markAllPresentDraft()"
      [disabled]="loading || applying || !markableCount"
    >
      {{ 'ATTENDANCE_MANUAL.MARK_ALL_PRESENT' | translate }}
    </button>
    <button
      type="button"
      class="btn btn-secondary btn-sm"
      (click)="clearDraft()"
      [disabled]="loading || applying || !hasPending"
    >
      {{ 'ATTENDANCE_MANUAL.CLEAR_DRAFT' | translate }}
    </button>
    <button
      type="button"
      class="btn btn-primary btn-sm"
      (click)="applyDraft()"
      [disabled]="loading || applying || !hasPending"
    >
      {{ (applying ? 'ATTENDANCE_MANUAL.APPLYING' : 'ATTENDANCE_MANUAL.APPLY') | translate }}
      <span *ngIf="hasPending && !applying">({{ pendingCount }})</span>
    </button>
    <span class="draft-hint muted" *ngIf="hasPending">{{ 'ATTENDANCE_MANUAL.DRAFT_HINT' | translate }}</span>
  </div>

  <div class="alert alert-error" *ngIf="errorMessage">{{ errorMessage | translate }}</div>

  <div class="card detail-card" *ngIf="loading && !report">
    <div class="empty-state">{{ 'COMMON.LOADING' | translate }}</div>
  </div>

  <div class="card detail-card" *ngIf="!loading && !selectedClassId">
    <div class="empty-state">{{ 'ATTENDANCE_MANUAL.SELECT_CLASS_HINT' | translate }}</div>
  </div>

  <div class="card table-card" *ngIf="report">
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th class="index-col">#</th>
            <th>{{ 'STUDENTS.NAME' | translate }}</th>
            <th class="center-col">{{ 'ATTENDANCE_DASH.DAY_STATUS' | translate }}</th>
            <th class="center-col">{{ 'ATTENDANCE.IN' | translate }}</th>
            <th class="center-col">{{ 'ATTENDANCE.OUT' | translate }}</th>
            <th class="actions-col center-col">{{ 'COMMON.ACTIONS' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let row of filteredRows; let i = index"
            [class]="'row-' + displayStatus(row)"
            [class.row-draft]="isPending(row)"
          >
            <td class="index-col">{{ i + 1 }}</td>
            <td>
              <strong>{{ studentName(row) }}</strong>
            </td>
            <td class="center-col">
              <span
                class="status-badge"
                [attr.data-status]="isPending(row) ? 'present' : displayStatus(row)"
                [class.draft]="isPending(row)"
              >
                {{ dayStatusKey(displayStatus(row)) | translate }}
                <span *ngIf="isPending(row)" class="draft-tag">{{ 'ATTENDANCE_MANUAL.DRAFT' | translate }}</span>
              </span>
            </td>
            <td class="center-col time-cell">
              <ng-container *ngIf="row.checkIn">
                <span>{{ formatTime(row.checkIn.at) }}</span>
                <span
                  class="timing"
                  *ngIf="lateEnabled"
                  [attr.data-status]="timingStatus(row.checkIn.status)"
                >
                  {{ timingKey(row.checkIn.status) | translate }}
                </span>
              </ng-container>
              <span class="draft-time" *ngIf="!row.checkIn && isPending(row)">{{ 'ATTENDANCE_MANUAL.PENDING' | translate }}</span>
              <span *ngIf="!row.checkIn && !isPending(row)">—</span>
            </td>
            <td class="center-col time-cell">
              <ng-container *ngIf="row.checkOut">
                <span>{{ formatTime(row.checkOut.at) }}</span>
                <span
                  class="timing"
                  *ngIf="lateEnabled"
                  [attr.data-status]="timingStatus(row.checkOut.status)"
                >
                  {{ timingKey(row.checkOut.status) | translate }}
                </span>
              </ng-container>
              <span *ngIf="!row.checkOut">—</span>
            </td>
            <td class="actions-col center-col">
              <div class="row-actions">
                <ng-container *ngIf="!row.checkIn?.at">
                  <button
                    type="button"
                    class="btn btn-success btn-sm"
                    *ngIf="canMarkPresentDraft(row)"
                    (click)="markPresentDraft(row)"
                    [disabled]="loading || applying"
                  >
                    {{ 'ATTENDANCE_MANUAL.MARK_PRESENT' | translate }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm"
                    *ngIf="canMarkAbsentDraft(row)"
                    (click)="markAbsentDraft(row)"
                    [disabled]="loading || applying"
                  >
                    {{ 'ATTENDANCE_MANUAL.MARK_ABSENT' | translate }}
                  </button>
                </ng-container>
                <button
                  type="button"
                  class="btn btn-secondary btn-sm"
                  *ngIf="row.checkIn?.at"
                  (click)="mark(row, 'check_out')"
                  [disabled]="loading || isBusy(row) || !canCheckOut(row)"
                >
                  {{ 'ATTENDANCE.ACTION_CHECK_OUT' | translate }}
                </button>
              </div>
            </td>
          </tr>
          <tr *ngIf="!filteredRows.length">
            <td colspan="6" class="empty-row">{{ 'ATTENDANCE_MANUAL.EMPTY' | translate }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>
